import { useAuth0 } from '@auth0/auth0-react';
import { useTextureAuth } from './textureAuthContext';
import { decodeJwt } from 'jose';
import { useMemo } from 'react';

/**
 * Unified authentication hook that works with both Auth0 and texture tokens.
 *
 * This hook provides a consistent interface for accessing user authentication state regardless of whether the
 * user is authenticated via Auth0 or texture tokens.
 *
 * For Auth0-specific functionality (login, logout, etc.), continue to use useAuth0() directly. This hook
 * should only be used when you need the basic user info and authentication status.
 *
 * For texture token authentication, this hook uses purely client-side decoding of the token to set the
 * isAuthenticated status and extract the user ID. The token is only verified server-side when making API
 * requests, until that point the authentication status should be considered provisional.
 *
 * The authentication type is determined explicitly from the texture auth context, which gets its state
 * from the TextureAuthProvider based on token extraction methods and query parameters.
 *
 * @returns {Object} Authentication state
 * @returns {Object|null} user - User object with 'sub' property (userId)
 * @returns {boolean} isAuthenticated - Whether the user is authenticated
 * @returns {boolean} isLoading - Whether authentication is still loading
 * @returns {string} authType - The authentication type being used ('auth0' or 'texture')
 */
export function useAuth() {
  const { user: auth0User, isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading } = useAuth0();
  const { textureToken, isTextureAuth, isLoading: textureIsLoading } = useTextureAuth();

  // Determine which authentication method is active based on texture auth context
  // The texture auth context is the authoritative source for determining auth type
  const authType = isTextureAuth ? 'texture' : 'auth0';

  // Decode texture token to get user info if using texture auth
  // Memoize textureUser to maintain stable reference
  const textureUser = useMemo(() => {
    if (!isTextureAuth || !textureToken) {
      return null;
    }

    try {
      const decoded = decodeJwt(textureToken);
      return {
        sub: decoded.sub, // userId from texture token
      };
    } catch (error) {
      console.error('Error decoding texture token:', error);
      return null;
    }
  }, [isTextureAuth, textureToken]);

  // Memoize the entire return object to ensure stable references
  return useMemo(() => {
    const user = isTextureAuth ? textureUser : auth0User;
    const isAuthenticated = isTextureAuth ? !!textureUser : auth0IsAuthenticated;
    const isLoading = isTextureAuth ? textureIsLoading : auth0IsLoading;

    return {
      user,
      isAuthenticated,
      isLoading,
      authType,
    };
  }, [isTextureAuth, textureUser, auth0User, auth0IsAuthenticated, textureIsLoading, auth0IsLoading, authType]);
}
